import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * Interface for navigation history items
 */
export interface NavigationHistoryItem {
  url: string;
  module: string;
  interfaceType?: string;
  timestamp: Date;
  state?: any;
}

@Injectable({
  providedIn: 'root'
})
export class NavigationHistoryService {
  private historyStack: NavigationHistoryItem[] = [];
  private currentStateSubject = new BehaviorSubject<NavigationHistoryItem | null>(null);
  
  // Observable that components can subscribe to see current navigation state
  public currentState$ = this.currentStateSubject.asObservable();

  constructor(private router: Router) {
    // Subscribe to router events to track URL changes
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      const url = event.urlAfterRedirects;
      const module = this.getModuleFromUrl(url);
      
      // Only add to history if it's a different URL
      if (this.historyStack.length === 0 || this.historyStack[this.historyStack.length - 1].url !== url) {
        this.addToHistory(url, module);
      }
    });
  }

  /**
   * Extract module name from URL
   */
  private getModuleFromUrl(url: string): string {
    // Remove leading slash
    const path = url.startsWith('/') ? url.substring(1) : url;
    
    // Split the path to get the first segment
    const segments = path.split('/');
    
    if (segments.length > 0 && segments[0]) {
      return segments[0];
    }
    
    return 'home'; // Default to home for '/' path
  }

  /**
   * Add a new entry to navigation history
   */
  public addToHistory(url: string, module: string, interfaceType?: string, state?: any): void {
    const historyItem: NavigationHistoryItem = {
      url,
      module,
      interfaceType,
      timestamp: new Date(),
      state
    };
    
    this.historyStack.push(historyItem);
    this.currentStateSubject.next(historyItem);
    
    // Keep history size manageable
    if (this.historyStack.length > 20) {
      this.historyStack.shift();
    }
    
    // For debugging
    console.log('Navigation history updated:', this.historyStack);
  }

  /**
   * Go back to the previous navigation state
   * @returns true if successfully navigated back, false if history is empty
   */
  public goBack(): boolean {
    // Need at least 2 items in history to go back (current and previous)
    if (this.historyStack.length < 2) {
      return false;
    }
    
    // Remove the current state
    this.historyStack.pop();
    
    // Get the previous state
    const previousState = this.historyStack[this.historyStack.length - 1];
    this.currentStateSubject.next(previousState);
    
    return true;
  }

  /**
   * Track interface changes within a module
   * @param moduleType The current module (text, video, image)
   * @param interfaceType The interface within the module
   * @param state Optional state data to store with this navigation point
   */
  public trackInterfaceChange(moduleType: string, interfaceType: string, state?: any): void {
    const currentPath = this.router.url;
    this.addToHistory(currentPath, moduleType, interfaceType, state);
  }

  /**
   * Get previous item in navigation history
   * @returns Previous navigation item or null if none exists
   */
  public getPreviousItem(): NavigationHistoryItem | null {
    if (this.historyStack.length < 2) {
      return null;
    }
    
    return this.historyStack[this.historyStack.length - 2];
  }

  /**
   * Clear navigation history
   */
  public clearHistory(): void {
    this.historyStack = [];
    this.currentStateSubject.next(null);
  }

  /**
   * Get full navigation history
   */
  public getHistory(): NavigationHistoryItem[] {
    return [...this.historyStack];
  }
} 